import { yupResolver } from '@hookform/resolvers/yup'
import { Box, IconButton } from '@mui/material'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomDialog from 'components/dialogs/CustomDialog'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { snackbarService } from 'services/snackbarService'
import * as yup from 'yup';
import { useAuth } from 'context/AuthContext'
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { colors } from 'styles/colors'
import { useChangePassword } from 'hooks/useUserAuth'

const validationSchema = yup.object({
  old_password: yup.string().min(8).required(),
  new_password: yup.string().min(8).required(),
});

interface Props {
  open: boolean,
  setOpen: (value: boolean) => void;
}

export default function ChangePasswordDialog({
  open,
  setOpen,
}: Props) {
  const { user } = useAuth();
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      old_password: '',
      new_password: '',
    }
  });

  const {
    handleSubmit,
    formState: { isValid },
    register,
    reset,
  } = methods;

  const { mutate: updateMutation, isLoading } = useChangePassword(
    (data: any) => {
      if (data.status_code == 200) {
        snackbarService.showSnackbar('Your password has been changed successfully', 'success');
        onClose();
      } else if (data.status_code == 618) {
        snackbarService.showSnackbar('Make sure your current password is correct', 'error');
      } else {
        snackbarService.showSnackbar(data?.data, 'error');
      }
    },
    (error: any) => {
      console.log('error', error);
    }
  )

  const onSubmit = (data: any) => {
    updateMutation({
      userId: user.id,
      dto: data,
    });
  };

  const onClose = () => {
    setOpen(false);
    setTimeout(() => {
      reset({
        old_password: '',
        new_password: '',
      });
      setShowOldPassword(false);
      setShowNewPassword(false);
    }, 500)
  }

  return (
    <CustomDialog
      open={open}
      title={`Change password`}
      alignText='center'
      onClose={onClose}
      width={300}
      content={
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >

            {/* Current password */}
            <Box mt={3}>
              <CustomFormTextInput
                name={'old_password'}
                register={register}
                placeholder='Current password'
                floatingLabel='Current password'
                required
                type={showOldPassword ? 'text' : 'password'}
                endIcon={
                  <Box mr={0.5}>
                    <IconButton
                      onClick={() => setShowOldPassword((prev) => !prev)}
                      edge="end"
                    >
                      {!showOldPassword ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22, color: colors.primary }} />}
                    </IconButton>
                  </Box>
                }
              />
            </Box>

            {/* New password */}
            <Box mt={2}>
              <CustomFormTextInput
                name={'new_password'}
                register={register}
                placeholder='New password'
                floatingLabel='New password'
                required
                type={showNewPassword ? 'text' : 'password'}
                endIcon={
                  <Box mr={0.5}>
                    <IconButton
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      edge="end"
                    >
                      {!showNewPassword ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22, color: colors.primary }} />}
                    </IconButton>
                  </Box>
                }
              />
            </Box>

            <Box my={2}>
              <CustomButton
                text={'Change'}
                color='primary'
                fullWidth
                type='submit'
                size='large'
                loading={isLoading}
                disabled={!isValid}
              />
            </Box>

          </Box>
        </FormProvider>
      }
    />
  )
}
